import { PaylinkCustomerInfo, PaylinkResponse } from 'skipify-types';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { toast } from '../../../util/toast';
import {
  useCreatePaylinkCustomerMutation,
  useUpdatePaylinkCustomerMutation,
} from '../../../store/modules/merchant/api';
import {
  useCreatePaylinkMutation,
  useSendPaylinkMutation,
  paylinkApi,
  useAttachFileMutation,
} from '../../../store/modules/paylink/api';
import { CreatePaylinkBody } from '../../../store/modules/paylink/types';
import { isGenericServerError, isServerError } from '../../../util/helpers';
import Logger from '../../../util/logger';

type UsePaylinkSubmitProps = {
  merchantId: string;
  onClose?: () => void;
};

export const usePaylinkSubmit = ({ merchantId, onClose }: UsePaylinkSubmitProps) => {
  const [createPaylink, { isLoading: isLoadingCreate }] = useCreatePaylinkMutation();
  const [sendPaylink, { isLoading: isLoadingSend }] = useSendPaylinkMutation();
  const [createPaylinkCustomer, { isLoading: isLoadingCreateCustomer }] =
    useCreatePaylinkCustomerMutation();
  const [updatePaylinkCustomer, { isLoading: isLoadingUpdateCustomer }] =
    useUpdatePaylinkCustomerMutation();

  const [attachFile, { isLoading: isLoadingAttachment }] = useAttachFileMutation();

  const [serverValidationError, setServerValidationError] = useState<string | null>(null);

  const dispatch = useDispatch();

  const createCustomer = useCallback(
    async (
      customer: CreatePaylinkBody['customer'],
    ): Promise<
      | { paylinkCustomerId: string; errorMessage: null; error: false }
      | { paylinkCustomerId: null; errorMessage: string | null; error: true }
    > => {
      if (customer.customerId) {
        return { paylinkCustomerId: customer.customerId, errorMessage: null, error: false };
      }

      try {
        const createdCustomer = await createPaylinkCustomer({
          merchantId,
          ...customer,
        }).unwrap();
        return {
          paylinkCustomerId: createdCustomer.payLinkCustomerId,
          errorMessage: null,
          error: false,
        };
      } catch (err) {
        Logger.error('Error creating customer', err);
        let errorMessage: string | null = null;
        if (isServerError(err) && err.data.statusCode === 409) {
          errorMessage =
            'Customer already exists and you might not have access to this customer. Please contact your store manager.';
        }
        return { paylinkCustomerId: null, errorMessage, error: true };
      }
    },
    [createPaylinkCustomer, merchantId],
  );

  const onPaylinkSubmit = useCallback(
    async (
      createPaylinkBody: CreatePaylinkBody,
      saveCustomerInformation?: boolean,
      attachment?: File | null,
      isSavingCustomerRequired?: boolean,
    ) => {
      setServerValidationError(null);
      const { customer } = createPaylinkBody;
      if (customer.customerId && Object.keys(customer).length !== 1) {
        // This is a saved customer missing information needed to create a Paylink. So we will
        // update the customer information and then create the Paylink.
        try {
          await updatePaylinkCustomer(
            customer as Partial<PaylinkCustomerInfo> & { customerId: string },
          ).unwrap();

          // let create go through before refetching
          setTimeout(() => {
            dispatch(paylinkApi.util.invalidateTags([{ type: 'Paylink', id: 'ALL' }]));
          }, 100);
        } catch (err) {
          let errorMessage = 'Something went wrong while updating the customer information.';
          if (isServerError(err) && err.data.statusCode === 409) {
            const extraField = customer.email ? 'email' : 'phone';
            errorMessage += ` A customer with this ${extraField} already exists and you might not have access to this customer. Please contact your store manager.`;
          }
          toast.error(errorMessage);
          throw err;
        }
      }

      let customerCreateErrorMessage: string | null = null;
      let paylinkCustomerId = customer.customerId;
      if (!paylinkCustomerId && (saveCustomerInformation || isSavingCustomerRequired)) {
        const {
          paylinkCustomerId: createdCustomerId,
          errorMessage,
          error,
        } = await createCustomer(customer);
        if (error) {
          if (isSavingCustomerRequired) {
            const snackbarErrorMessage = errorMessage
              ? `An error occurred while creating the customer: ${errorMessage}`
              : 'Something went wrong while creating the customer. Please try again later.';
            toast.error(snackbarErrorMessage);
            throw new Error(snackbarErrorMessage);
          }
          customerCreateErrorMessage = errorMessage;
        } else {
          paylinkCustomerId = createdCustomerId;
        }
      }

      let createResponse: PaylinkResponse;
      try {
        createResponse = await createPaylink({
          ...createPaylinkBody,
          customer: paylinkCustomerId ? { customerId: paylinkCustomerId } : customer,
        }).unwrap();
      } catch (err) {
        let errorMessage =
          'Something went wrong while creating the Paylink, please try again later';
        if (isServerError(err) && err.data.statusCode === 400) {
          setServerValidationError(err.data.message);
          throw new Error(err.data.message);
        }
        if (isGenericServerError(err) && err.status === 422) {
          errorMessage =
            'This merchant needs additional setup in order to send paylinks.' +
            ' Please select a different merchant or contact support@skipify.com for assistance.';
        }
        toast.error(errorMessage);
        throw err;
      }

      const { paylinkId, sendEmail, sendSms } = createResponse;

      if (attachment) {
        try {
          await attachFile({
            paylinkId,
            file: attachment,
          }).unwrap();
        } catch {
          const errorMessage = 'File was unable to be attached to the request.';
          toast.error(errorMessage);
        }
      }

      try {
        await sendPaylink({ paylinkId, sendEmail, sendSms }).unwrap();
      } catch (err) {
        if (isServerError(err) && err.data.statusCode === 400) {
          setServerValidationError(err.data.message);
          throw new Error(err.data.message);
        }
        const errorMessage =
          'Something went wrong while sending the Paylink, please try again later';
        toast.error(errorMessage);
        throw err;
      }

      let successMessage = 'Paylink successfully created.';
      if (customerCreateErrorMessage) {
        successMessage = `Paylink successfully created, but customer information could not be saved. ${customerCreateErrorMessage}`;
      }

      toast.success(successMessage);
      if (onClose) {
        onClose();
      }
    },
    [
      attachFile,
      createCustomer,
      createPaylink,
      dispatch,
      onClose,
      sendPaylink,
      updatePaylinkCustomer,
    ],
  );

  return {
    onPaylinkSubmit,
    isLoading:
      isLoadingCreate ||
      isLoadingSend ||
      isLoadingCreateCustomer ||
      isLoadingUpdateCustomer ||
      isLoadingAttachment,
    serverValidationError,
  };
};
